import { useCallback, useState } from "react";
import { useMenuData } from "../../../menu-provider/MenuDataProvider";
import { useMenuIdFromURL } from "../../../hooks/menuIdFromURL";

export type GoLiveStep = {
  id: string;
  label: string;
  done: boolean;
  action?: { label: string; to: string; onActivate?: () => void };
};

const qrVisitedStorageKey = (menuId: string) => `goLiveQrVisited:${menuId}`;

export const useGoLiveSteps = (): {
  steps: GoLiveStep[];
  isComplete: boolean;
  restaurantName: string;
} => {
  const { categories, restaurantName } = useMenuData();
  const menuId = useMenuIdFromURL();
  const storageKey = qrVisitedStorageKey(menuId);

  const [qrVisited, setQrVisited] = useState(
    () => localStorage.getItem(storageKey) === "true"
  );

  const markQrVisited = useCallback(() => {
    localStorage.setItem(storageKey, "true");
    setQrVisited(true);
  }, [storageKey]);

  const menuPath = `/dashboard/menu/${menuId}`;
  const hasCategory = categories.length > 0;
  const hasItem = categories.some((category) => category.items.length > 0);

  const steps: GoLiveStep[] = [
    { id: "create-menu", label: "Create your menu", done: true },
    {
      id: "add-category",
      label: "Add a category",
      done: hasCategory,
      action: { label: "Create your first category", to: `${menuPath}/update` },
    },
    {
      id: "add-item",
      label: "Add a menu item",
      done: hasItem,
      action: { label: "Add your first item", to: `${menuPath}/update` },
    },
    {
      id: "place-qr",
      label: "Generate & place a QR code",
      done: qrVisited,
      action: {
        label: "Generate a QR code",
        to: `${menuPath}/qr-generation`,
        onActivate: markQrVisited,
      },
    },
  ];

  const isComplete = steps.every((step) => step.done);

  return { steps, isComplete, restaurantName };
};
